import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { generatePath, matchPath, useLocation, useNavigate } from 'react-router-dom';

import { useSmallScreen } from 'utils/hooks';
import { userSelectors } from 'reducers';
import { blockDistributorNavigation } from 'utils/helpers';
import { elementScrollIntoViewPolyfill } from 'seamless-scroll-polyfill';

import * as icons from 'mdi-material-ui';
import { AppBar, Breadcrumbs, Link } from '@mui/material';
import { tunnelPaths } from 'utils/constants';

elementScrollIntoViewPolyfill();

const scrollIntoViewOptions = {
    behavior: 'smooth',
    inline: 'center',
    block: 'nearest',
};

const PhasesNav = props => {
    const { phases, currentPhase, lastPhaseIndex } = props;

    const { pathname } = useLocation();
    const navigate = useNavigate();
    const phasesRef = useRef([]);
    const isMobileScreen = useSmallScreen('sm'); // < 600px
    const isSmallScreen = useSmallScreen('md'); // < 900px

    const loggedIn = useSelector(userSelectors.getLoggedInStatus);
    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const currentPath = useMemo(
        () => tunnelPaths.find(path => matchPath(path, pathname)),
        [pathname]
    );

    useEffect(() => {
        phasesRef.current = phasesRef.current.slice(0, phases.length);
    }, [phases]);

    useEffect(() => {
        if ((isSmallScreen || isMobileScreen) && phases.length) {
            const phaseIndex = phases.findIndex(ph => ph.id === currentPhase.id);
            phasesRef.current[phaseIndex].scrollIntoView(scrollIntoViewOptions);
        }
    }, [currentPhase, isSmallScreen, isMobileScreen, phases]);

    const handleClick = useCallback(
        (stepId, index) => e => {
            e.preventDefault();
            if (isSmallScreen) {
                phasesRef.current[index].scrollIntoView(scrollIntoViewOptions);
            }
            if (!loggedIn) return;
            const targetPath = generatePath(currentPath, { stepId });
            // TODO: try a solution to call changes verifier here
            navigate(targetPath);
        },
        [isSmallScreen, loggedIn, currentPath, navigate]
    );

    const investingSumStepsIndex = useMemo(() => {
        return phases.findIndex(phase =>
            phase.tunnelSteps.find(
                tunnelStep => tunnelStep.step.id === 30 || tunnelStep.step.id === 6
            )
        );
    }, [phases]);

    const getNavDisabledStatus = useCallback(
        (tunnelPhase, index) => {
            let disabled = !loggedIn ? index > 0 : index > lastPhaseIndex;

            if (tunnelSubscription?.indirectUntransferred) disabled = false;
            if (blockDistributorNavigation(internalUser) || !tunnelPhase.tunnelSteps.length) {
                disabled = true;
            }

            // investingSum is mandatory even in indirect subscriptions
            if (
                investingSumStepsIndex !== -1 &&
                index > investingSumStepsIndex &&
                !tunnelSubscription?.investmentCharacteristics?.investingSum &&
                tunnelSubscription?.indirectUntransferred
            ) {
                disabled = true;
            }

            return disabled;
        },
        [tunnelSubscription, lastPhaseIndex, internalUser, loggedIn, investingSumStepsIndex]
    );

    const renderPhase = useCallback(
        (tunnelPhase, index) => {
            let selected = !loggedIn ? index === 0 : currentPhase?.id === tunnelPhase.id;
            const disabled = getNavDisabledStatus(tunnelPhase, index);

            return (
                <div key={tunnelPhase.id}>
                    <Link
                        href="/"
                        ref={el => (phasesRef.current[index] = el)}
                        underline="none"
                        color={selected ? 'primary' : 'inherit'}
                        sx={{
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                            color: disabled ? 'grey.400' : 'common.black',
                            fontWeight: selected ? 600 : 'normal',
                            pointerEvents: disabled ? 'none' : 'auto',
                            textDecoration: disabled ? 'none' : 'inherit',
                            '&:hover, &:active, &:visited, &:focus': {
                                color: disabled
                                    ? 'grey.400'
                                    : selected
                                    ? 'common.black'
                                    : 'inherit',
                            },
                        }}
                        onClick={handleClick(tunnelPhase.tunnelSteps[0]?.step.id, index)}
                    >
                        {tunnelPhase.phase.name}
                    </Link>
                </div>
            );
        },
        [loggedIn, handleClick, currentPhase, getNavDisabledStatus]
    );

    return (
        <AppBar
            sx={theme => ({
                background: `${theme.palette.grey[200]}`,
                justifyContent: 'center',
                height: `${theme.constants.phaseBarHeight}px`,
                top: `${theme.constants.appBarHeight}px`,
            })}
            elevation={0}
        >
            <Breadcrumbs
                sx={{
                    '& .MuiBreadcrumbs-ol': {
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                        padding: '0 20px',
                        overflowX: 'scroll',
                        alignItems: 'center',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                            height: 0,
                            background: 'transparent',
                        },
                        scrollbarWidth: 'none',
                        '@media (max-width: 960px)': {
                            padding: '0 40%',
                            justifyContent: 'normal',
                        },
                    },
                    '& .MuiBreadcrumbs-li': {
                        display: 'flex',
                        '@media (max-width: 960px)': {
                            '&:last-child': {
                                '&::after': {
                                    content: '""',
                                    width: 350,
                                },
                            },
                        },
                        '@media (max-width: 600px)': {
                            '&:last-child': {
                                '&::after': {
                                    content: '""',
                                    width: 150,
                                },
                            },
                        },
                    },
                }}
                separator={<icons.MenuRight />}
                aria-label="breadcrumb"
            >
                {phases.map(renderPhase)}
            </Breadcrumbs>
        </AppBar>
    );
};

PhasesNav.propTypes = {
    phases: PropTypes.array,
    currentPhase: PropTypes.object,
    lastPhaseIndex: PropTypes.number,
};

export default PhasesNav;
